import { useEffect, useRef, useState } from 'react';
import {
  loadPlaybackMode,
  PLAYBACK_MODE_DESCRIPTION,
  PLAYBACK_MODE_LABEL,
  savePlaybackMode,
  type PlaybackMode,
} from '../lib/playbackMode.js';

type Props = {
  name: string;
  onNameChange: (name: string) => void;
  /** 部屋のURLを直接開いたが名前が未設定だった場合のコード */
  pendingRoomCode: string | null;
  onEnterRoom: (code: string) => void;
};

export function Landing({ name, onNameChange, pendingRoomCode, onEnterRoom }: Props) {
  const [code, setCode] = useState(pendingRoomCode ?? '');
  const [busy, setBusy] = useState<'create' | 'join' | null>(null);
  const [mode, setMode] = useState<PlaybackMode>(loadPlaybackMode);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (pendingRoomCode && !name.trim()) nameRef.current?.focus();
  }, [pendingRoomCode, name]);

  const trimmedName = name.trim();
  const canProceed = trimmedName.length > 0;

  // ボタンを押せない理由は必ず画面に出す。黙って disabled にすると、
  // 名前を入れずにコードだけ入力した人が理由に気づけない。
  const joinBlockedReason = !canProceed
    ? '参加するには、まず名前を入れてください。'
    : code.length > 0 && code.length !== 4
      ? '部屋コードは4桁です。'
      : null;

  /**
   * 全角数字（１２３４）を半角に寄せてから数字だけ残す。
   * NFKC 正規化を挟まないと、日本語入力のまま打った数字が丸ごと捨てられ、
   * 「入力したのに何も表示されない」状態になる。
   */
  const normalizeCode = (raw: string): string =>
    raw.normalize('NFKC').replace(/\D/g, '').slice(0, 4);

  const createRoom = async () => {
    if (!canProceed) return;
    setBusy('create');
    setError(null);
    try {
      const res = await fetch('/api/rooms', { method: 'POST' });
      if (!res.ok) throw new Error('部屋を作れませんでした');
      const json = (await res.json()) as { code: string };
      onEnterRoom(json.code);
    } catch (err) {
      setError(err instanceof Error ? err.message : '部屋を作れませんでした');
    } finally {
      setBusy(null);
    }
  };

  const joinRoom = async () => {
    if (!canProceed) return;
    const target = normalizeCode(code);
    if (!/^\d{4}$/.test(target)) {
      setError('4桁の数字を入れてください');
      return;
    }
    setBusy('join');
    setError(null);
    try {
      const res = await fetch(`/api/rooms/${target}`);
      if (!res.ok) throw new Error(`部屋 ${target} は見つかりませんでした`);
      onEnterRoom(target);
    } catch (err) {
      setError(err instanceof Error ? err.message : '参加できませんでした');
    } finally {
      setBusy(null);
    }
  };

  return (
    <main className="landing">
      <header className="landing-head">
        <h1>Jam Station</h1>
        <p>
          Spotify でも Apple Music でも、みんなの曲をひとつのキューに集めて
          順番に流す。ラウンドロビンなので、誰の曲もちゃんとかかる。
        </p>
      </header>

      <section className="card">
        <label className="field">
          <span className="field-label">名前</span>
          <input
            ref={nameRef}
            className="input"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="キューに表示される名前"
            maxLength={24}
            autoComplete="nickname"
          />
        </label>

        {!canProceed && (
          <p className="hint">
            {pendingRoomCode ? (
              <>
                部屋 <strong>{pendingRoomCode}</strong> に入るには、まず名前を入れてください。
              </>
            ) : (
              '名前を入れると、部屋を作ったり参加したりできます。'
            )}
          </p>
        )}

        <button
          className="btn btn-primary btn-block"
          onClick={createRoom}
          disabled={!canProceed || busy !== null}
        >
          {busy === 'create' ? '作成中...' : 'Jam をつくる'}
        </button>

        <div className="divider">
          <span>または</span>
        </div>

        <label className="field">
          <span className="field-label">部屋コード</span>
          <input
            className="input input-code"
            value={code}
            // 日本語IMEだと全角数字で入ることがある。NFKC で半角に寄せてから
            // 数字以外を落とす。これが無いと「１２３４」が丸ごと消えて、
            // 入力したのに空欄のままに見える。
            onChange={(e) => setCode(normalizeCode(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void joinRoom();
            }}
            placeholder="0000"
            inputMode="numeric"
            autoComplete="off"
          />
        </label>

        <button
          className="btn btn-block"
          onClick={joinRoom}
          disabled={!canProceed || code.length !== 4 || busy !== null}
        >
          {busy === 'join' ? '確認中...' : '参加する'}
        </button>

        {joinBlockedReason && <p className="hint">{joinBlockedReason}</p>}
        {error && <p className="error">{error}</p>}
      </section>

      <section className="card">
        <div className="field-label">この端末の再生方法</div>
        <div className="mode-choice">
          {(['audio', 'iframe'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className={`mode-option ${mode === option ? 'mode-option-active' : ''}`}
              onClick={() => {
                setMode(option);
                savePlaybackMode(option);
              }}
            >
              <span className="mode-option-name">{PLAYBACK_MODE_LABEL[option]}</span>
              <span className="mode-option-desc">
                {PLAYBACK_MODE_DESCRIPTION[option]}
              </span>
            </button>
          ))}
        </div>
        <p className="hint hint-small">
          あとから変えられます。音を出さない端末には関係ありません。
        </p>
      </section>

      <footer className="landing-foot">
        <p>
          ログインは要りません。名前だけで参加できます。
          曲は各プラットフォームのリンクを貼るか、曲名で検索して追加します。
        </p>
      </footer>
    </main>
  );
}
